import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  Box,
  CountUp,
  formatAmount,
  HeaderTooltip,
  Icon,
  Skeleton,
  useStyle,
} from '@bonanza-social/moonlight';
import {
  StackNavigationProp,
  useFocusEffect,
  useNavigation,
} from '@bonanza-social/navigation';
import { usePrevious, useViewDimensions } from '@bonanza-social/toolkit/hooks';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import {
  getCoinBalance,
  selectCoinBalance,
  selectCoinBalanceDataState,
} from '../../state/coinBalance/coinBalance.slice';
import { selectStatus, useStatusPerks } from '@bonanza-social/status';
import { formatCompact } from '@bonanza-social/toolkit/utils';
import { CoinBalanceHeaderItemProps } from './CoinBalanceHeaderItem.types';

const staticStyles = {
  position: 'relative',
  height: 40,
  variant: 'default',
  width: 'auto',
  backgroundColor: 'secondaryBackgroundDefault',
  borderRadius: 'radius-200',
  padding: 'space-300',
  gap: 'space-100',
  flexDirection: 'column',
  justifyContent: 'center',
} as const;

const MAX_CAPTION_BALANCE = 99999999;

export const coinBalanceWallet: { current: View | null } = {
  current: null,
};

function CoinBalanceHeaderItem({ level }: CoinBalanceHeaderItemProps) {
  const dispatch = useDispatch();
  const coinBalance = useSelector(selectCoinBalance);
  const coinBalanceDataState = useSelector(selectCoinBalanceDataState);
  const { ref, onLayout } = useViewDimensions('COIN_BALANCE_HEADER_ITEM');

  const pressableStyle = useStyle({ ...staticStyles });
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const [initialBalance, setInitialBalance] = useState<number>();
  const prevCoinBalance = usePrevious(coinBalance);
  const previousLevel = usePrevious(level);
  const [tooltipCoinDiff, setTooltipCoinDiff] = useState(0);
  const [showTooltipLevelUp, setShowTooltipLevelUp] = useState(false);
  const { levelUpMultiplier } = useStatusPerks();
  const status = useSelector(selectStatus);

  const balanceDiff = useMemo(() => {
    if (prevCoinBalance || (initialBalance === 0 && prevCoinBalance === 0)) {
      return coinBalance - prevCoinBalance;
    } else {
      return 0;
    }
  }, [coinBalance, initialBalance, prevCoinBalance]);

  useEffect(() => {
    const prevBalance = prevCoinBalance || 0;

    if (
      (initialBalance === undefined && coinBalanceDataState === 'fulfilled') ||
      coinBalance < prevBalance
    ) {
      setInitialBalance(coinBalance);
    }
  }, [coinBalance, coinBalanceDataState, initialBalance, prevCoinBalance]);

  useEffect(() => {
    if (level === undefined || previousLevel === undefined) {
      return;
    }

    if (level !== previousLevel) {
      setShowTooltipLevelUp(true);
    }
  }, [level, previousLevel]);

  const coins = useMemo(
    () =>
      coinBalanceDataState?.match('fulfilled|refreshing') ? (
        <CountUp
          variant={coinBalance > MAX_CAPTION_BALANCE ? 'badge' : 'caption'}
          color="darkSurfacePrimary"
          start={initialBalance ?? 0}
          useVisualIncrement={true}
          useFrameRestriction={true}
          end={coinBalance}
          increment={coinBalance - (initialBalance ?? 0)}
          visualIncrement={1}
          duration={2000}
          formatValue={formatAmount}
        />
      ) : (
        <Skeleton width={30} />
      ),
    [coinBalance, coinBalanceDataState, initialBalance],
  );

  useEffect(() => {
    if (balanceDiff > 0 && balanceDiff !== coinBalance) {
      setTooltipCoinDiff(balanceDiff);
    }
  }, [balanceDiff, coinBalance]);

  const hideBalanceTooltip = useCallback(() => {
    setTooltipCoinDiff(0);
  }, []);

  const hideLevelUpTooltip = useCallback(() => {
    setShowTooltipLevelUp(false);
  }, []);

  const handleClick = () => {
    navigation.navigate('Shop');
  };

  useFocusEffect(
    useCallback(() => {
      dispatch(getCoinBalance());
    }, [dispatch]),
  );

  useFocusEffect(
    useCallback(() => {
      coinBalanceWallet.current = ref.current;
    }, [ref]),
  );

  return (
    <Pressable
      ref={ref}
      style={pressableStyle}
      onLayout={onLayout}
      onPress={handleClick}
      testID="coin-balance-header-item"
    >
      <Box
        position="absolute"
        zIndex={10}
        left="space-0"
        right="space-0"
        alignItems="center"
        justifyContent="center"
        flexDirection="row"
        gap="space-100"
      >
        {status && levelUpMultiplier ? (
          <HeaderTooltip
            visible={showTooltipLevelUp}
            text={`x${levelUpMultiplier}`}
            gradient={`status${status.playerStatus}Background`}
            absolute={false}
            hide={hideLevelUpTooltip}
          />
        ) : null}
        <HeaderTooltip
          visible={Boolean(tooltipCoinDiff)}
          hide={hideBalanceTooltip}
          text={`+${levelUpMultiplier ? formatCompact(tooltipCoinDiff) : tooltipCoinDiff}`}
          absolute={false}
        />
      </Box>
      <Box
        flexDirection="row"
        alignItems="center"
        gap="space-200"
        position="relative"
      >
        <Icon
          name="bonanzaCoinGold"
          gradient="accentBackground"
          width="space-400"
          height="space-400"
        />
        {coins}
      </Box>
    </Pressable>
  );
}

export default CoinBalanceHeaderItem;
